import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import useTokenInfo from "../../../hooks/useTokenInfo";
import useStrictBackBlock from "../../../bridges/useStrictBackBlock";
import checkOn from "../../../assets/icons/Select/check_checked.svg";
import checkOff from "../../../assets/icons/Select/check_unchecked.svg";
import WizLoadingCircle from "../../../assets/components/WizLoadingCircle";

const defaultAiModel = {
  enabled: false,
  modelType: "",
  label: "",
  prompt: "",
  refImagePath: "",
};

const GalleryLoading = () => {
  const location = useLocation();
  const data = location.state;
  const navigate = useNavigate();
  const storeBusinessNumber = data.data.store_business_number;
  const [stepStates, setStepStates] = useState(["idle", "idle", "idle"]);
  const style = data.style;
  const aiModel = data.aiModel || defaultAiModel;

  const { tokenInfo, fetchTokenInfo, deductToken } = useTokenInfo();

  useEffect(() => {
    if (data.custom_menu) {
      localStorage.setItem("customMenu", data.custom_menu);
    }

    const timers = [];

    const fetchData = async (currentTimers, token) => {
      setStepStates(["loading", "idle", "idle"]);
      currentTimers.push(setTimeout(() => setStepStates(["done", "loading", "idle"]), 1200));
      currentTimers.push(setTimeout(() => setStepStates(["done", "done", "loading"]), 2200));

      const formData = new FormData();
      const normalizedAiModel = {
        ...defaultAiModel,
        ...aiModel,
      };

      if (style === "AI 모델 합성" && (!normalizedAiModel.enabled || !normalizedAiModel.modelType)) {
        alert("AI 모델 합성 옵션을 다시 설정해 주세요.");
        setStepStates(["idle", "idle", "idle"]);
        return;
      }

      if (data.imageFile) {
        formData.append("image", data.imageFile);
      } else if (data.imageUri) {
        try {
          const imageUrl = String(data.imageUri || "").trim();
          const cacheBustedUrl = imageUrl.includes("?")
            ? `${imageUrl}&_=${Date.now()}`
            : `${imageUrl}?_=${Date.now()}`;

          const imgRes = await fetch(cacheBustedUrl, { cache: "no-store" });
          if (!imgRes.ok) {
            throw new Error(`image fetch failed: ${imgRes.status}`);
          }

          const ctype = imgRes.headers.get("content-type") || "";
          if (!ctype.toLowerCase().startsWith("image/")) {
            throw new Error(`non-image content-type: ${ctype}`);
          }

          const imgBlob = await imgRes.blob();
          const imgFile = new File([imgBlob], "banner.jpg", {
            type: imgBlob.type || "image/jpeg",
          });
          formData.append("image", imgFile);
        } catch (e) {
          console.error("원격 이미지 로드 실패:", e);
          alert("이미지 정보를 불러오지 못했습니다.");
          navigate(-1);
          return;
        }
      } else {
        alert("이미지 정보가 없습니다.");
        navigate(-1);
        return;
      }

      formData.append("channel", data.channel);
      formData.append("title", data.title);
      formData.append("age", data.age);
      formData.append("style", style);
      formData.append("aspect_ratio", "4:5");
      formData.append("bg_prompt", data.bg_prompt || "");
      formData.append("filter", data.filter || "");
      formData.append("category", data.category);
      formData.append("custom_menu", data.custom_menu);
      formData.append("register_tag", localStorage.getItem("registerTag"));
      formData.append("store_name", data.store_name);
      formData.append("road_name", data.road_name);
      formData.append("district_name", data.district_name);
      formData.append("main", data.main);
      formData.append("temp", data.temp);

      if (style === "AI 모델 합성") {
        formData.append("ai_model_type", normalizedAiModel.modelType || "");
        formData.append("ai_model_prompt", normalizedAiModel.prompt || "");

        if (normalizedAiModel.refImagePath) {
          try {
            const refUrl = String(normalizedAiModel.refImagePath || "").trim();
            const cacheBustedRefUrl = refUrl.includes("?")
              ? `${refUrl}&_=${Date.now()}`
              : `${refUrl}?_=${Date.now()}`;

            const refRes = await fetch(cacheBustedRefUrl, { cache: "no-store" });
            const refBlob = await refRes.blob();
            const refFile = new File([refBlob], `${normalizedAiModel.modelType || "ai_model"}.png`, {
              type: refBlob.type || "image/png",
            });
            formData.append("ai_model_ref_image", refFile);
          } catch (e) {
            console.error("AI 모델 레퍼런스 이미지 로드 실패:", e);
          }
        }
      } else {
        formData.append("ai_model_type", "");
        formData.append("ai_model_prompt", "");
      }

      const userCopy = typeof data.customText === "string" ? data.customText : "";
      formData.append("custom_text", userCopy);

      try {
        const start = Date.now();
        const response = await axios.post(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/manual/app/camera`,
          formData,
        );
        const end = Date.now();

        const elapsed = end - start;
        const minDisplayTime = 3000;
        const remaining = elapsed < minDisplayTime ? minDisplayTime - elapsed : 0;

        let tokenAll = null;
        try {
          tokenAll = await deductToken(token);
        } catch (err) {
          console.error("토큰 차감 실패:", err);
        }

        currentTimers.push(setTimeout(() => {
          setStepStates(["done", "done", "done"]);
        }, remaining));

        const res = response?.data || {};
        if (res?.title === "이벤트" && !(data.customText && String(data.customText).trim()) && res?.event_title) {
          res.copyright = `제목 : ${res.event_title ?? ""} 내용 : ${res.copyright ?? ""}`;
        }

        currentTimers.push(setTimeout(() => {
          navigate("/manual/marketing/gallery/result", {
            state: {
              state: res,
              data: data.data,
              origin: data.imageFile || data.imageUri || null,
              userCopy,
              tokenAll,
              aiModel: normalizedAiModel,
            },
          });
        }, remaining + 800));
      } catch (error) {
        console.error("요청 실패:", error);
        alert("이미지 생성에 실패했습니다.");
        navigate(-1);
      }
    };

    const tokenRun = async () => {
      try {
        const total = await fetchTokenInfo();
        if (!Number.isFinite(total) || total <= 0) {
          alert("이용 가능한 토큰이 없습니다. 결제 페이지로 이동합니다.");
          return navigate("/myticket", { state: { storeBusinessNumber } });
        }

        await fetchData(timers, total);
      } catch (err) {
        console.error("토큰 검증 중 오류:", err);
        alert("토큰 정보를 불러오는 중 오류가 발생했습니다.");
        navigate("/myticket", { state: { storeBusinessNumber } });
      }
    };

    void tokenRun();

    return () => timers.forEach(clearTimeout);
  }, []);

  const renderStep = (status, label) => {
    if (status === "loading") {
      return (
        <div className="flex items-center space-x-2 text-text-subtler text-b1">
          <img src={checkOff} alt="" />
          <span>{label} 분석 중</span>
        </div>
      );
    }

    if (status === "done") {
      return (
        <div className="flex items-center space-x-2 text-text-primary text-b1">
          <img src={checkOn} alt="" />
          <span>{label} 분석 완료</span>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2 text-text-subtler text-b1">
        <img src={checkOff} alt="" className="opacity-40" />
        <span>{label} 분석 예정</span>
      </div>
    );
  };

  useStrictBackBlock(true);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col justify-center items-center">
      <WizLoadingCircle />
      <p className="text-center text-t2 text-text-default pb-[24px] ">AI 이미지를 생성하고 있습니다.</p>
      <div className="text-center text-b1 text-text-subtle pb-[12px]">
        {renderStep(stepStates[0], "홍보 메뉴")}
        {renderStep(stepStates[1], "홍보 내용")}
        {renderStep(stepStates[2], "스타일")}
      </div>
      <div className="fixed bottom-[64px]">
        <p className="text-c2 text-text-subtle pb-[4px]">1개의 토큰을 사용하고 있어요.</p>
        <div className="text-center text-c1 text-text-primary">
          잔여 토큰
          <div className="inline-flex">{String(tokenInfo.total)}</div>
        </div>
      </div>
    </div>
  );
};

export default GalleryLoading;
